import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserDetails } from 'src/app/models/userDetails';
import { UserService } from 'src/app/services/user-service';

export interface FeedbackContext {
  source?: string;        // e.g. 'sidebar', 'search-result'
  registration?: string;  // plate registration as displayed
  term?: string;          // active search term, if any
  pageUrl?: string;       // overrides window.location.href if supplied
}

interface FeedbackDialogData {
  context?: FeedbackContext;
}

@Component({
  selector: 'pla-feedback-dialog-modal',
  templateUrl: './feedback-dialog-modal.component.html',
  styleUrls: ['./feedback-dialog-modal.component.css'],
})
export class FeedbackDialogModalComponent implements OnInit {
  public submitted: boolean = false;
  public fetching: boolean = true;
  public loading: boolean = false;
  public error?: string = undefined;
  public context?: FeedbackContext;
  // honeypot — hidden from real users; if it ever has a value the submit drops
  // silently. Cheap deterrent for the simplest crawlers; not a real spam shield.
  // Email is OPTIONAL — only validated for format when the user types something.
  // The sentinel below is used on submit if the field is empty, so the existing
  // Plato /feedback {email, summary} contract never receives an empty string.
  private static readonly NO_EMAIL_SENTINEL = 'no-reply-provided@platex.co.uk';

  public feedbackForm = new FormGroup({
    email: new FormControl('', [Validators.email]),
    summary: new FormControl('', [Validators.required]),
    website: new FormControl(''),
  });

  constructor(
    private userService: UserService,
    @Optional() @Inject(MAT_DIALOG_DATA) data?: FeedbackDialogData,
  ) {
    this.context = data?.context;
    this.userService.getNullableUserDetails((_: UserDetails) => {
      this.fetching = false;
      if (_ != null) this.feedbackForm.controls['email'].setValue(_.email);
    });
  }

  ngOnInit(): void {}

  public submit(): void {
    this.error = undefined;
    var raw = this.feedbackForm.getRawValue() as {
      email: string;
      summary: string;
      website: string;
    };
    // Honeypot tripped — drop silently so a bot sees the same success path.
    if (raw.website && raw.website.length > 0) {
      this.submitted = true;
      return;
    }
    // Summary is the only mandatory field. Email is optional.
    if (raw.summary == undefined || raw.summary == '') return;
    // If the user typed an email but it failed format validation, refuse.
    // (Empty is fine — we substitute the sentinel below.)
    var userEmail = (raw.email || '').trim();
    if (userEmail.length > 0 && this.feedbackForm.controls.email.invalid) return;
    var emailProvided = userEmail.length > 0;
    var payload = {
      email: emailProvided ? userEmail : FeedbackDialogModalComponent.NO_EMAIL_SENTINEL,
      summary: this.buildSummary(raw.summary, this.context, emailProvided),
    };
    this.loading = true;
    this.userService.sendFeedback(payload, (status: boolean) => {
      this.loading = false;
      if (!status) {
        this.feedbackForm.clearValidators();
        this.error = 'Something went wrong';
      } else {
        this.submitted = true;
      }
    });
  }

  // Folds a context block into the summary text so the existing Plato
  // /feedback endpoint (which only accepts {email, summary}) carries the
  // extra fields through to the Jira ticket body without any backend change.
  // When emailProvided=false we flag it in the body so a reviewer doesn't
  // mistake the sentinel address (no-reply-provided@platex.co.uk) for a real
  // contact address.
  private buildSummary(userMessage: string, ctx?: FeedbackContext, emailProvided: boolean = true): string {
    var lines: string[] = ['[PlateX context]'];
    if (!emailProvided) lines.push('Email: [No email provided]');
    if (ctx) {
      if (ctx.source)       lines.push(`Source: ${ctx.source}`);
      if (ctx.registration) lines.push(`Registration: ${ctx.registration}`);
      if (ctx.term)         lines.push(`Search term: ${ctx.term}`);
    }
    var pageUrl = (ctx && ctx.pageUrl) || (typeof window !== 'undefined' ? window.location.href : undefined);
    if (pageUrl)          lines.push(`Page: ${pageUrl}`);
    lines.push(`Reported at: ${new Date().toISOString()}`);
    lines.push('');
    lines.push('[User message]');
    lines.push(userMessage);
    return lines.join('\n');
  }
}
